import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import MediaPicker from './MediaPicker';
import {
  selectItemById,
  selectImages,
  selectAudios,
  selectVideos,
} from '../../utils/projectSelectors';
import MediaCard from './MediaCard';

// ---------- Value types ----------
interface OptionMediaValue {
  source: 'option';
  id: string;
  name: string;
  preview: string;
  url: string;
}

interface ProjectMediaValue {
  source: 'project';
  id: string;
  name: string;
  key?: string;
  location?: string;
  url?: string;
}

type FormValue = string | OptionMediaValue | ProjectMediaValue | null;

interface FieldSpec {
  type: string;
  options?: Array<Record<string, unknown>>;
  value?: string;
  max_length?: number;
  placeholder?: string;
  index?: number;
}

interface FieldRendererProps {
  spec: FieldSpec;
  value: FormValue;
  onChange: (value: FormValue) => void;
  disabled?: boolean;
}

// ---------- Type guards ----------
const isProjectRef = (v: unknown): v is ProjectMediaValue =>
  !!v && typeof v === 'object' && (v as Record<string, unknown>).source === 'project';
const isOptionRef = (v: unknown): v is OptionMediaValue =>
  !!v && typeof v === 'object' && (v as Record<string, unknown>).source === 'option';

// ---------- Small leaf components ----------
interface SelectFieldProps {
  spec: FieldSpec;
  value: FormValue;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function SelectField({ spec, value, onChange, disabled }: SelectFieldProps) {
  const opts = useMemo(() => {
    return (spec.options || []).map((o: Record<string, unknown> | string) =>
      typeof o === 'string'
        ? { label: o, val: o }
        : {
            label: (o.name as string) ?? String(o.value ?? ''),
            val: String(o.value ?? o.name ?? ''),
          },
    );
  }, [spec.options]);

  return (
    <div className="grid gap-1.5">
      <select
        className="border border-border/60 rounded-md bg-card px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-ring/30"
        disabled={disabled}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      >
        {opts.map((o) => (
          <option key={o.label + o.val} value={o.val}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TextFieldProps {
  spec: FieldSpec;
  value: FormValue;
  onChange: (value: string) => void;
  disabled?: boolean;
  inputType: string;
}

function TextField({ spec, value, onChange, disabled, inputType }: TextFieldProps) {
  const max = spec?.max_length ?? null;
  const str = value == null ? '' : String(value);
  const len = str.length;

  return (
    <div className="grid gap-1.5">
      <input
        className="border border-border/60 rounded-md bg-card px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-ring/30"
        type="text"
        inputMode={inputType === 'number' ? 'numeric' : undefined}
        value={str}
        onChange={(e) => onChange(e.target.value)}
        placeholder={spec?.placeholder || ''}
        maxLength={max ?? undefined}
        disabled={disabled}
      />
      {max != null && (
        <div className="text-xs text-muted-foreground">
          <span>
            {len}/{max}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------- OptionMediaField ----------
interface OptionMediaFieldProps {
  type: string;
  spec: FieldSpec;
  value: FormValue;
  onChange: (value: FormValue) => void;
  disabled?: boolean;
}

function OptionMediaField({ type, spec, value, onChange, disabled }: OptionMediaFieldProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const optionItems = useMemo(() => {
    return (spec.options || []).map((opt: Record<string, unknown>) => ({
      ...opt,
      url: opt.preview as string,
      id: opt.name as string,
    }));
  }, [spec.options]);

  const selectedOption = isOptionRef(value) ? value : null;
  const name = selectedOption?.name;
  const label = type === 'image' ? 'image' : type === 'audio' ? 'audio' : 'video';

  return (
    <div className="grid gap-1.5">
      {selectedOption ? (
        <MediaCard
          type={type as 'image' | 'video' | 'audio'}
          url={selectedOption.preview}
          name={name || ''}
          onPickerOpen={() => setPickerOpen(true)}
          canSelect={false}
          viewOnly={disabled}
        />
      ) : (
        <div className="flex items-center gap-2.5 max-md:flex-col max-md:items-stretch">
          <div className="text-muted-foreground">No {label} selected</div>
          {!disabled && (
            <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover" onClick={() => setPickerOpen(true)}>
              Select
            </button>
          )}
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          type={type as 'image' | 'video' | 'audio'}
          onClose={() => setPickerOpen(false)}
          items={optionItems as Array<{ id: string; name: string; signedUrl?: string; url?: string }>}
          onSelect={(item) => {
            onChange({ ...item, source: 'option' } as unknown as OptionMediaValue);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------- ProjectMediaField ----------
interface ProjectMediaFieldProps {
  type: string;
  value: FormValue;
  onChange: (value: FormValue) => void;
  disabled?: boolean;
}

function ProjectMediaField({ type, value, onChange, disabled }: ProjectMediaFieldProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const projectItems = useSelector(
    type === 'image' ? selectImages : type === 'audio' ? selectAudios : selectVideos,
  );

  let media: Record<string, unknown> | undefined;

  if (isProjectRef(value)) {
    if (value.id) {
      media = projectItems.find((it: Record<string, unknown>) => it.id === value.id);
    }
    if (value.name && !media) {
      media = projectItems.find((it: Record<string, unknown>) => it.name === value.name);
    }
  }

  useEffect(() => {
    if (disabled) return;
    if (!media) return;
    if (!isProjectRef(value)) return;

    if (value.id && value.key) return;
    onChange({
      source: 'project',
      id: media.id as string,
      name: media.name as string,
      key: media.key as string,
      location: media.location as string,
      url: media.signedUrl as string,
    });
  }, [
    disabled,
    media?.id,
    media?.key,
    onChange,
    isProjectRef(value) ? value?.id : undefined,
    isProjectRef(value) ? value?.key : undefined,
  ]);

  return (
    <div className="grid gap-1.5">
      {media ? (
        <MediaCard
          type={type as 'image' | 'video' | 'audio'}
          url={media.signedUrl as string}
          name={media.name as string}
          onPickerOpen={() => setPickerOpen(true)}
          canSelect={false}
          viewOnly={disabled}
        />
      ) : (
        <div className="flex items-center gap-2.5 max-md:flex-col max-md:items-stretch">
          <div className="text-muted-foreground">No {type} selected</div>
          {!disabled && (
            <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover" onClick={() => setPickerOpen(true)}>
              Select
            </button>
          )}
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          type={type as 'image' | 'video' | 'audio'}
          onClose={() => setPickerOpen(false)}
          items={projectItems}
          onSelect={(item: Record<string, unknown>) => {
            onChange({
              source: 'project',
              id: item.id as string,
              name: item.name as string,
              url: (item.signedUrl || item.url) as string,
              location: item.location as string,
              key: item.key as string,
            });
            setPickerOpen(false);
          }}
          onClear={() => {
            onChange(null);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------- MediaField (router) ----------
interface MediaFieldProps {
  spec: FieldSpec;
  value: FormValue;
  onChange: (value: FormValue) => void;
  disabled?: boolean;
}

function MediaField({ spec, value, onChange, disabled }: MediaFieldProps) {
  const type = spec?.type;
  const hasOptions = Array.isArray(spec?.options) && spec.options.length > 0;

  if (hasOptions) {
    return (
      <OptionMediaField
        type={type}
        spec={spec}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  return <ProjectMediaField type={type} value={value} onChange={onChange} disabled={disabled} />;
}

// ---------- Public renderer ----------
export default function FieldRenderer({ spec, value, onChange, disabled = false }: FieldRendererProps) {
  const type = spec?.type || 'text';
  const hasOptions = Array.isArray(spec?.options) && spec.options.length > 0;

  if (type === 'text' || type === 'number') {
    if (hasOptions) {
      return <SelectField spec={spec} value={value} onChange={onChange as (v: string) => void} disabled={disabled} />;
    }
    return (
      <TextField
        spec={spec}
        value={value}
        onChange={onChange as (v: string) => void}
        disabled={disabled}
        inputType={type}
      />
    );
  }

  if (['image', 'audio', 'video'].includes(type)) {
    return <MediaField spec={spec} value={value} onChange={onChange} disabled={disabled} />;
  }

  return null;
}
